import React, { Component, Fragment } from 'react';
import { fromJS, Map, List } from 'immutable';
import { Flex, Box } from 'grid-styled';
import PropTypes from 'prop-types';
import decode from 'decode-html';
import qs from 'qs';
import styled, { css } from 'styled-components';

import connect from '../store/connect';
import { analyticsDetailEvent } from '../util/analyticEvents';
import { addCartItem, setViewstate, fetchProduct } from '../actions/actionCreators';
import { mapToObject } from '../util/mapToObject';
import { axiosSingleton } from '../api/generalApi';

import Page from './Page';
import ErrorPage from './ErrorPage';
import NotFoundPage from './NotFoundPage';

import ProductPageGallery from '../components/productPage/ProductPageGallery/ProductPageGallery';
import ProductPageInfo from '../components/productPage/ProductPageInfo/ProductPageInfo';
import ProductPageDescription from '../components/productPage/ProductPageDescription/ProductPageDescription';
import ProductQuantitySelector from '../components/productPage/ProductQuantitySelector/ProductQuantitySelector';
import ProductUSP from '../components/productPage/ProductUSP/ProductUSP';
import ProductConfiguration from '../components/ProductConfiguration';
import ProductList from '../components/ProductList';
import LinedHeading from '../components/LinedHeading';
import RenderPrice from '../components/RenderPrice';
import StickyButton from '../components/StickyButton';
import SwipeWrapper from '../components/SwipeWrapper';
import History from '../history';
import { SITE_ROOT_URL, CART_ADD_URL } from '../constants/endpoints';

import { color } from '../styles/helpers/variables';
import { Heading } from '../styles/Heading';
import { Button } from '../styles/Button';
import { SubHeading } from '../styles/SubHeading';
import { ListParent } from '../styles/ListParent';
import { ListItem } from '../styles/ListItem';
import { Label } from '../styles/Label';
import { Container } from '../styles/Container';
import { WashIcon } from '../components/WashIcon';
import { fontFamilyBody, showOnPrint } from '../styles/helpers/mixins';
import { MATERIAL_IMAGE, BARE_IMAGE } from '../constants/imageTypes';
import { Conditional } from '../components/Conditional';
import ProductPageComplimentaryProducts from '../components/productPage/ProductPageComplimentaryProducts/ProductPageComplimentaryProducts';

const createProductImageList = (currentProduct, selectedVariant) => {
  const productImage = {
    url: SITE_ROOT_URL + selectedVariant.getIn(['mainImage', 'url']),
    alt: selectedVariant.getIn(['mainImage', 'alt']),
    type: selectedVariant.getIn(['mainImage', 'type']),
    needsBackground: true
  };
  const baseProductImage = {
    url: SITE_ROOT_URL + currentProduct.getIn(['mainImage', 'url']),
    alt: currentProduct.getIn(['mainImage', 'alt']),
    needsBackground: true
  };
  const productImages = currentProduct.get('images')
    ? currentProduct
        .get('images')
        .skip(1)
        .map(image => {
          if (image.get('type') === 'Image' || image.get('type') === BARE_IMAGE) {
            return {
              url: SITE_ROOT_URL + image.get('url'),
              alt: image.get('alt'),
              type: image.get('type'),
              needsBackground: image.get('type') === BARE_IMAGE
            };
          }
          return false;
        })
        .toJS()
    : [];
  const variantImages = selectedVariant.get('images')
    ? selectedVariant
        .get('images')
        .skip(1)
        .map(image => {
          if (image.get('type') === 'Image' || image.get('type') === BARE_IMAGE) {
            return {
              url: SITE_ROOT_URL + image.get('url'),
              alt: image.get('alt'),
              type: image.get('type'),
              needsBackground: image.get('type') === BARE_IMAGE
            };
          }
          return false;
        })
        .toJS()
    : [];
  const images = [
    productImage.type === MATERIAL_IMAGE ? baseProductImage : {},
    productImage,
    ...variantImages,
    ...productImages
  ].filter(item => Object.keys(item).length);
  return images;
};

class ProductPage extends Component {
  constructor(props) {
    super(props);
    this.buyButtonActiveTimeout = null;
    this.state = {
      stickyBoxScrollPositionChanged: false,
      scrollPosition: window.pageYOffset,
      selectedOptions: {},
      buyButtonActive: false,
      quantity: 1,
      recentlyViewedProducts: []
    };
    this.setProductVariant = this.setProductVariant.bind(this);
    this.setProductOption = this.setProductOption.bind(this);
    this.buyButtonHandler = this.buyButtonHandler.bind(this);
    this.stickyBoxScrollHandler = this.stickyBoxScrollHandler.bind(this);
    this.renderRelatedProducts = this.renderRelatedProducts.bind(this);
    this.renderRecentlyViewedProducts = this.renderRecentlyViewedProducts.bind(this);
    this.specificationsOpened = this.specificationsOpened.bind(this);
    this.onChangeQuantity = this.onChangeQuantity.bind(this);
    this.onIncreaseQuantity = this.onIncreaseQuantity.bind(this);
    this.onDecreaseQuantity = this.onDecreaseQuantity.bind(this);
  }

  componentDidMount() {
    const variantsLoaded = this.props.currentProduct.getIn(['variants', 0], Map()).has('url');

    this.props.fetchProduct({
      id: this.props.match.params.productId,
      url: this.props.match.url
    });

    if (!this.props.currentProduct.isEmpty() && variantsLoaded) {
      this.setDefaultOptions(this.props.currentProduct, this.props.location);
    }

    window.addEventListener('scroll', this.stickyBoxScrollHandler);
  }

  componentWillReceiveProps(nextProps) {
    const nextVariantsLoaded = nextProps.currentProduct.getIn(['variants', 0], Map()).has('url');

    if (this.props.match.params.productId !== nextProps.match.params.productId) {
      this.props.fetchProduct({
        id: nextProps.match.params.productId,
        url: nextProps.match.url
      });
      this.setState(() => ({ quantity: 1 }));
    }

    if (!this.props.currentProduct.equals(nextProps.currentProduct) && nextVariantsLoaded) {
      this.setDefaultOptions(nextProps.currentProduct, nextProps.location);
      analyticsDetailEvent('productDetail', nextProps.currentProduct);
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // Don't update component if only scrollPosition has been updated
    return this.state.scrollPosition === nextState.scrollPosition;
  }

  componentDidUpdate(prevProps) {
    const nextVariantsLoaded = this.props.currentProduct.getIn(['variants', 0], Map()).has('url');
    if (nextVariantsLoaded && !this.props.currentProduct.equals(prevProps.currentProduct)) {
      this.addToListOfRecentlyViewedProducts();
      this.fetchRecentlyViewedProducts();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.stickyBoxScrollHandler);
  }

  onChangeQuantity(quantity) {
    this.setState(() => ({ quantity: (quantity >= 999 ? 999 : quantity) || 1 }));
  }

  onIncreaseQuantity() {
    this.setState(() => ({ quantity: this.state.quantity >= 999 ? 999 : this.state.quantity + 1 }));
  }

  onDecreaseQuantity() {
    this.setState(() => ({ quantity: this.state.quantity - 1 || 1 }));
  }

  setDefaultOptions(currentProduct, location) {
    const queryStringOptions = qs.parse(location.search.substr(1));

    const activeVariantId = currentProduct
      .get('variants', List())
      .filter(variant => variant.get('url') === location.pathname)
      .getIn([0, 'id'], currentProduct.get('defaultVariantId'));

    this.setState({
      variantId: activeVariantId
    });

    if (!currentProduct.isEmpty()) {
      const baseProductDefaultOptions = currentProduct
        .get('configurations', List())
        .reduce((memory, item) => Object.assign({ [item.get('name')]: item.get('defaultOption') }, memory), {});

      const currentVariantConfigurations = currentProduct
        .get('variants', List())
        .find(variant => variant.get('url') === location.pathname)
        .get('configurations');

      // check if the current data model has configurations options, before saying it does
      const variantProductsDefaultOptions = currentVariantConfigurations
        ? currentProduct
            .get('variants', List())
            .filter(variant => variant.get('url') === location.pathname)
            .getIn([0, 'configurations'])
            .reduce((memory, item) => Object.assign({ [item.get('name')]: item.get('defaultOption') }, memory), {})
        : {};

      // Go through selected product options in the URL:s query string and filter out any options that does not exist on the current product
      const filteredQueryStringOptions = fromJS(queryStringOptions)
        .filter((item, index) => {
          // See if current option in the URL:s query string matches an option on the base product
          const baseConfigurations = currentProduct
            .get('configurations', List())
            .find(configuration => configuration.get('name') === index);

          if (baseConfigurations) {
            return baseConfigurations.get('options', List()).find(option => option.get('id') === item);
          }

          const currentProductConfigurations =
            currentVariantConfigurations &&
            currentVariantConfigurations.find(configuration => configuration.get('name') === index);

          // See if current option in the URL:s query string matches an option on a product variant
          const currentProductVariant =
            (currentProductConfigurations &&
              currentProductConfigurations.get('options', List()).find(option => option.get('id') === item)) ||
            Map();

          return !!currentProductVariant;
        })
        .toJS();

      const concatResultOfAllDefaultOptions = Object.assign(
        baseProductDefaultOptions,
        variantProductsDefaultOptions,
        filteredQueryStringOptions
      );

      this.setState({
        selectedOptions: concatResultOfAllDefaultOptions
      });

      History.replace(this.generateProductUrl(currentProduct, activeVariantId, concatResultOfAllDefaultOptions));
    }
  }

  setProductVariant(id) {
    this.setState({
      variantId: id
    });

    // removed this line since it seemed to cause issues with swapping selected productVariant...
    // this.setDefaultOptions(this.props.currentProduct);

    if (this.reactSwipe) {
      this.reactSwipe.jumpToFirst();
    }

    if (id && !this.props.currentProduct.isEmpty() && !this.props.location.pathname.includes(id)) {
      History.replace(this.generateProductUrl(undefined, id));
    }
  }

  setProductOption(id, type) {
    const selectedOption = { [type]: id };
    const newSelectedOptions = Object.assign(this.state.selectedOptions, selectedOption);

    this.setState({
      selectedOptions: newSelectedOptions
    });

    if (newSelectedOptions && !this.props.currentProduct.isEmpty()) {
      History.replace(this.generateProductUrl(undefined, undefined, newSelectedOptions));
    }
  }

  getProductSpecificationsList() {
    // a function that takes the specifications from the base product and merges them with the specifications from the selected variant product
    if (!this.state.variantId || !this.props.currentProduct) {
      return false;
    }

    const baseSpecifications = this.props.currentProduct.get('specifications', List());

    const variantSpecificationsBase = this.props.currentProduct
      .get('variants', List())
      .find(variant => variant.get('id') === this.state.variantId);

    const variantSpecifications =
      typeof variantSpecificationsBase === 'undefined' ? {} : variantSpecificationsBase.get('specifications', List());

    return baseSpecifications
      .mergeDeep(variantSpecifications)
      .map((item, index) => {
        if (!item.get('key') || !item.get('value')) return false;

        return (
          <ListItem key={index}>
            <Label>{item.get('key')}:</Label> {item.get('value')}
          </ListItem>
        );
      })
      .toArray();
  }

  getRecentlyViewedProducts() {
    if (this.localStorageAvailable()) {
      const currentProductId = this.props.currentProduct.get('id');
      const recentlyViewedProducts = JSON.parse(window.localStorage.getItem('recentlyViewedProducts')) || [];
      const recentlyViewedProductsExcludingCurrentProduct = recentlyViewedProducts.filter(
        product => product !== currentProductId
      );
      return recentlyViewedProductsExcludingCurrentProduct;
    }
    return [];
  }

  localStorageAvailable() {
    if (typeof window === 'undefined') {
      return false;
    }
    const test = 'localStorageAvailable';
    try {
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);
      return true;
    } catch (error) {
      return false;
    }
  }

  fetchRecentlyViewedProducts() {
    const recentlyViewedProducts = this.getRecentlyViewedProducts();
    if (recentlyViewedProducts.length) {
      axiosSingleton
        .get('/products/', { params: { productIds: recentlyViewedProducts } })
        .then(({ data: recentlyViewedProductData }) => {
          const sortedRecentlyViewedProductData = recentlyViewedProductData.sort(
            (a, b) => recentlyViewedProducts.indexOf(b.id) - recentlyViewedProducts.indexOf(a.id)
          );
          this.setState(() => ({ recentlyViewedProducts: mapToObject(sortedRecentlyViewedProductData) }));
        })
        .catch(error => {
          throw error;
        });
    }
  }

  addToListOfRecentlyViewedProducts() {
    const recentlyViewedProducts = this.getRecentlyViewedProducts();
    const productId = this.props.currentProduct.get('id');
    let updatedRecentlyViewedProducts = [...recentlyViewedProducts, productId];
    const numberOfStoredRecentlyViewedProducts = 6 + 1;
    if (updatedRecentlyViewedProducts.length > numberOfStoredRecentlyViewedProducts) {
      updatedRecentlyViewedProducts = updatedRecentlyViewedProducts.slice(numberOfStoredRecentlyViewedProducts * -1);
    }
    window.localStorage.setItem('recentlyViewedProducts', JSON.stringify(updatedRecentlyViewedProducts));
  }

  productIdsToObjects(ids) {
    return ids
      .map(item => this.props.products.filter((x, index) => index === item))
      .toMap()
      .flatten(1);
  }

  productListToMap(productList) {
    return productList.toMap().mapEntries(entry => {
      const newEntry = entry;
      newEntry[0] = entry[1].get('id');
      return newEntry;
    });
  }

  buyButtonHandler(id) {
    clearTimeout(this.buyButtonActiveTimeout);
    this.setState(() => ({ buyButtonActive: true }));
    this.props.addCartItem({
      endpointUrl: CART_ADD_URL,
      body: { id, Quantity: this.state.quantity, Options: Object.values(this.state.activeAndSelectedOptions) }
    });
    this.setState(() => ({ quantity: 1 }));
    this.buyButtonActiveTimeout = setTimeout(() => this.setState(() => ({ buyButtonActive: false })), 1800);
    this.props.setViewstate('productAddedToCart', true);
  }

  stickyBoxScrollHandler() {
    const currentScrollPosition = window.pageYOffset;

    if (this.props.viewstate.get('pageWidth') >= 960) {
      if (this.stickyContent && !this.state.stickyBoxScrollPositionChanged && window.scrollTo) {
        setTimeout(() => {
          this.stickyContent.scrollTo(0, 0);
          this.setState({ stickyBoxScrollPositionChanged: true });
        }, 0);
      } else if (this.stickyContent && window.scrollBy) {
        const diff = this.state.scrollPosition - currentScrollPosition;
        this.stickyContent.scrollBy(0, -diff);
      }
    }

    this.setState({ scrollPosition: window.pageYOffset });
  }

  specificationsOpened() {
    setTimeout(() => {
      if (this.stickyContent.scrollHeight > this.stickyContent.parentElement.scrollHeight && Object.scrollTo) {
        this.stickyContent.scrollTo({
          left: 0,
          top: this.stickyContent.scrollHeight,
          behavior: 'smooth'
        });
      }
    }, 200);
  }

  generateProductUrl(
    currentProduct = this.props.currentProduct,
    variantId = this.state.variantId,
    selectedOptions = this.state.selectedOptions
  ) {
    const baseConfigurations = currentProduct
      .get('configurations', List())
      .toMap()
      .mapKeys((key, value) => value.get('name'));

    const variantConfigurations = currentProduct
      .get('variants', List())
      .find(variant => variant.get('id') === variantId)
      .get('configurations', List())
      .toMap()
      .mapKeys((key, value) => value.get('name'));

    const activeConfigurations = baseConfigurations.mergeDeep(variantConfigurations);

    const activeAndSelectedOptions = fromJS(selectedOptions).filter((item, index) =>
      Object.keys(activeConfigurations.toJS()).includes(index)
    );

    const isSelectedOptionsValid = activeAndSelectedOptions
      .map((option, index) => {
        const isValid = !!activeConfigurations.getIn([index, 'options']).find(x => x.get('id') === option);

        // the selected configuration option still exists on the new variant, so we'll keep it
        if (isValid) return option;

        // the selected configuration option does NOT exist on the new variant, revert to the defaultOption
        return activeConfigurations.getIn([index, 'defaultOption']);
      })
      .toJS();

    this.setState({ activeAndSelectedOptions: isSelectedOptionsValid });

    const configurationQueryString = qs.stringify(selectedOptions);

    return `${currentProduct
      .get('variants', List())
      .find(variant => variant.get('id') === variantId)
      .get('url')}?${configurationQueryString}`;
  }

  renderRelatedProducts() {
    if (!this.props.currentProduct.get('relatedProducts', []).size) {
      return false;
    }
    const products = this.productListToMap(this.props.currentProduct.get('relatedProducts'));
    return (
      <Container hideOnPrint productPage={!(this.props.viewstate.get('pageWidth') < 960)}>
        <LinedHeading noMargin bgColor={color.lightGrey}>
          Relaterade Produkter
        </LinedHeading>
        <ProductList data={products} />
      </Container>
    );
  }

  renderRecentlyViewedProducts() {
    if (!(this.state.recentlyViewedProducts && this.state.recentlyViewedProducts.size)) {
      return null;
    }
    const products = this.productListToMap(this.state.recentlyViewedProducts);
    return (
      <Container hideOnPrint productPage={!(this.props.viewstate.get('pageWidth') < 960)}>
        <LinedHeading noMargin bgColor={color.lightGrey}>
          Senaste visade produkter
        </LinedHeading>
        <ProductList columns={6} center data={products} />
      </Container>
    );
  }

  renderProductSpecifications(selectedVariant) {
    const washingInstructionsSize = selectedVariant.get('washingInstructions', List()).size;
    return (
      <ListParent>
        <Conditional show={!!selectedVariant.get('id')}>
          <ListItem>
            <Label>Artikelnummer:</Label> {selectedVariant.get('id')}
          </ListItem>
        </Conditional>

        <Conditional show={!!this.props.currentProduct.getIn(['brand', 'name'])}>
          <ListItem>
            <Label>Varumärke:</Label> {this.props.currentProduct.getIn(['brand', 'name'])}
          </ListItem>
        </Conditional>

        <Conditional show={!!this.props.currentProduct.getIn(['designer', 'name'])}>
          <ListItem>
            <Label>Formgivare:</Label> {this.props.currentProduct.getIn(['designer', 'name'])}
          </ListItem>
        </Conditional>

        {this.getProductSpecificationsList()}

        <Conditional show={washingInstructionsSize === 1}>
          <ListItem>
            <Label>Tvättråd:</Label>
            {selectedVariant.get('washingInstructions', List()).map(
              icon =>
                icon.get('value') !== '#tomt#' && (
                  <Fragment>
                    {` ${icon.get('value')}`}
                    <WashIcon id={icon.get('key')} />
                  </Fragment>
                )
            )}
          </ListItem>
        </Conditional>

        <Conditional show={washingInstructionsSize > 1}>
          <ListItem>
            <Label>Tvättråd:</Label>
            <WashList>
              {selectedVariant.get('washingInstructions', List()).map(
                icon =>
                  icon.get('value') !== '#tomt#' && (
                    <WashListItem>
                      {icon.get('value')}
                      <WashIcon id={icon.get('key')} />
                    </WashListItem>
                  )
              )}
            </WashList>
          </ListItem>
        </Conditional>
      </ListParent>
    );
  }

  render() {
    const { currentProduct, viewstate, request } = this.props;
    const { activeAndSelectedOptions } = this.state;
    const pageWidth = viewstate.get('pageWidth');

    if (request.get('statusCode') === 404) return <NotFoundPage />;
    if (request.get('status') === 'ERROR') return <ErrorPage />;
    if (!currentProduct.size || !this.state.variantId) return <LoadingProductPage />;

    const selectedVariant =
      currentProduct.get('variants', Map()).find(variant => variant.get('url') === this.props.location.pathname) ||
      Map();
    const productSpecifications = this.renderProductSpecifications(selectedVariant);

    const productImageUrl = SITE_ROOT_URL + selectedVariant.getIn(['mainImage', 'url'], Map());
    const productImageAlt = selectedVariant.getIn(['mainImage', 'alt'], Map());
    const productImageType = selectedVariant.getIn(['mainImage', 'type'], Map());
    const baseProductImageUrl = SITE_ROOT_URL + currentProduct.getIn(['mainImage', 'url'], Map());
    const baseProductImageAlt = currentProduct.getIn(['mainImage', 'alt'], Map());

    const complementaryProducts = this.productListToMap(currentProduct.get('complementaryProducts', List()));

    const relatedProducts = this.renderRelatedProducts();
    const recentlyViewedProducts = this.renderRecentlyViewedProducts();

    const allOptions = currentProduct
      .get('configurations', List())
      .toSet()
      .union(selectedVariant.get('configurations', List().toSet()))
      .toList();

    const optionsPrice = allOptions.map(item =>
      item
        .get('options')
        .find(x => x.get('id') === activeAndSelectedOptions[item.get('name')])
        .get('price')
    );

    const currentOptionsPrice = optionsPrice.reduce((memory, optionPrice) => memory + optionPrice, 0);

    if (pageWidth < 960) {
      return (
        <Page
          meta={selectedVariant.get('metaData')}
          canonical={currentProduct.get('url')}
          bgColor={color.lightGrey}
          lastBreadcrumbItem={{ title: currentProduct.get('name'), url: this.props.location.pathname }}
        >
          <SwipeWrapper
            ref={reactSwipe => {
              this.reactSwipe = reactSwipe;
            }}
            currentProduct={currentProduct}
            selectedVariant={selectedVariant}
            pageWidth={pageWidth}
            productImageUrl={productImageUrl}
            productImageAlt={productImageAlt}
            baseProductImageUrl={baseProductImageUrl}
            baseProductImageAlt={baseProductImageAlt}
            productImageType={productImageType}
          />

          <Container noPadding noPaddingBottom pageWidth={pageWidth} productPageMobileFirst>
            {currentProduct.getIn(['brand', 'url']) && currentProduct.getIn(['brand', 'name']) ? (
              <SubHeading.link
                to={currentProduct.getIn(['brand', 'url'])}
                uppercase
                color={color.black}
                productPageMoble
              >
                {currentProduct.getIn(['brand', 'name'])}
              </SubHeading.link>
            ) : (
              false
            )}
            <Heading productPageMoble>{selectedVariant.get('name')}</Heading>
          </Container>

          <Container pageWidth={pageWidth} pb={16}>
            <ProductConfiguration
              isMobileDevice
              location={this.props.location.pathname}
              data={currentProduct.get('variants', Map())}
              optionsData={allOptions}
              setProductVariant={this.setProductVariant}
              setProductOption={this.setProductOption}
              selected={this.state.variantId}
              selectedOptions={this.state.activeAndSelectedOptions}
              currentProduct={currentProduct}
              price={selectedVariant.get('price') + currentOptionsPrice}
            />
          </Container>

          <ProductPageInfo product={currentProduct} />

          <StickyButton
            success
            disabled={this.state.buyButtonActive}
            active={this.state.buyButtonActive}
            onClick={() => {
              this.buyButtonHandler(selectedVariant.get('id'));
            }}
            selectedVariant={selectedVariant}
            currentOptionsPrice={currentOptionsPrice}
            quantitySelector={
              <ProductQuantitySelector
                quantity={this.state.quantity}
                handleChange={this.onChangeQuantity}
                handleDecrease={this.onDecreaseQuantity}
                handleIncrease={this.onIncreaseQuantity}
                mobile
              />
            }
          />

          {complementaryProducts.size > 0 && (
            <Container pt={2} pb={28} pageWidth={pageWidth}>
              <ComplimentaryProductsContainer>
                <h2>Komplettera med</h2>
                <ProductPageComplimentaryProducts complementaryProducts={complementaryProducts} />
              </ComplimentaryProductsContainer>
            </Container>
          )}

          {relatedProducts}

          {recentlyViewedProducts}
        </Page>
      );
    }
    const numberOfProductConfigurations =
      (currentProduct.get('variants', Map()).toJS().length > 1 ? 1 : 0) + allOptions.toJS().length;
    return (
      <Page
        meta={selectedVariant.get('metaData')}
        canonical={currentProduct.get('url')}
        bgColor={color.lightGrey}
        lastBreadcrumbItem={{ title: currentProduct.get('name'), url: this.props.location.pathname }}
      >
        <Container padded pageWidth={pageWidth} productPage productPageFirst>
          <Flex>
            <MainProductContent width={5 / 12} pr={60} numberOfProductConfigurations={numberOfProductConfigurations}>
              {currentProduct.getIn(['brand', 'url']) && currentProduct.getIn(['brand', 'name']) && (
                <SubHeading.link
                  to={currentProduct.getIn(['brand', 'url'])}
                  color={color.black}
                  margin="0"
                  uppercase
                  productPage
                >
                  {currentProduct.getIn(['brand', 'name'])}
                </SubHeading.link>
              )}

              <Heading margin="0" productPage>
                {selectedVariant.get('name')}
              </Heading>

              <RenderPrice
                currentOptionsPrice={currentOptionsPrice}
                price={selectedVariant.get('price')}
                originalPrice={selectedVariant.get('originalPrice')}
                productPage
                fontWeight="700"
              />

              <ProductPageDescription description={decode(currentProduct.get('description', ''))} />

              <ProductConfiguration
                location={this.props.location.pathname}
                data={currentProduct.get('variants', Map())}
                optionsData={allOptions}
                setProductVariant={this.setProductVariant}
                setProductOption={this.setProductOption}
                selected={this.state.variantId}
                selectedOptions={this.state.activeAndSelectedOptions}
                currentProduct={currentProduct}
                price={selectedVariant.get('price') + currentOptionsPrice}
              />

              <ProductBuyContainer>
                <ProductQuantitySelector
                  quantity={this.state.quantity}
                  handleChange={this.onChangeQuantity}
                  handleDecrease={this.onDecreaseQuantity}
                  handleIncrease={this.onIncreaseQuantity}
                />

                <Button
                  success
                  buy
                  disabled={this.state.buyButtonActive}
                  active={this.state.buyButtonActive}
                  onClick={() => {
                    this.buyButtonHandler(selectedVariant.get('id'));
                  }}
                >
                  Lägg i varukorg
                </Button>
              </ProductBuyContainer>

              <ProductUSP deliveryInformation={selectedVariant.get('deliveryInformation')} />

              {complementaryProducts.size > 0 && (
                <ComplimentaryProductsContainer>
                  <h2>Komplettera med</h2>
                  <ProductPageComplimentaryProducts complementaryProducts={complementaryProducts} />
                </ComplimentaryProductsContainer>
              )}

              <PrintSpecifications>{productSpecifications}</PrintSpecifications>
            </MainProductContent>
            <Box width={7 / 12}>
              <ProductPageGallery
                images={createProductImageList(currentProduct, selectedVariant)}
                tags={selectedVariant.get('tags', List())}
                variantId={this.state.variantId}
              />
            </Box>
          </Flex>
        </Container>
        <ProductPageInfo product={currentProduct} />

        {relatedProducts}

        {recentlyViewedProducts}
      </Page>
    );
  }
}

const LoadingProductPage = styled.div`
  width: 100%;
  height: 1500px;
  background-color: #f2f2f2;
`;

const WashList = styled.ul`
  padding-left: 2em;
  list-style-type: disc;
`;

const WashListItem = styled.li`
  ${fontFamilyBody};
  line-height: 1.75;
  font-size: 14px;
  font-weight: normal;
  position: relative;
`;

const PrintSpecifications = styled.div`
  ${showOnPrint};
  margin: 30px 0 0;
`;

const MainProductContent = styled(Box)`
  ${props =>
    props.numberOfProductConfigurations === 0 &&
    css`
      @media screen and (min-width: 1180px) {
        padding-top: 30px;
      }
      @media screen and (min-width: 1440px) {
        padding-top: 60px;
      }
    `};
  @media print {
    width: 100% !important;
  }
`;

const ProductBuyContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 0 15px;
  @media screen and (min-width: 1150px) {
    flex-wrap: nowrap;
  }
  &:last-child {
    margin: 0;
  }
  > button {
    margin: 0;
  }
`;

const ComplimentaryProductsContainer = styled.div`
  margin: 30px 0 0;
  > h2 {
    margin: 0 0 5px;
    color: #000000;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }
`;

ProductPage.defaultProps = {
  currentProduct: Map(),
  products: Map(),
  request: Map(),
  viewstate: Map()
};

ProductPage.propTypes = {
  currentProduct: PropTypes.instanceOf(Map),
  products: PropTypes.instanceOf(Map),
  request: PropTypes.instanceOf(Map),
  viewstate: PropTypes.instanceOf(Map),
  setViewstate: PropTypes.func.isRequired,
  fetchProduct: PropTypes.func.isRequired,
  addCartItem: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired
};

export default connect(ProductPage, { addCartItem, setViewstate, fetchProduct }, (store, props) => ({
  currentProduct:
    store.products.data.find(product => product.get('url') === props.location.pathname.replace(/\/$/, '')) ||
    store.products.data.find(product =>
      product.get('variants', List()).find(variant => variant.get('url') === props.location.pathname.replace(/\/$/, ''))
    ),
  products: store.products.data,
  request: store.products.request.get(props.location.pathname.replace(/\/$/, '')),
  viewstate: store.viewstate
}));



// WEBPACK FOOTER //
// ./src/pages/ProductPage.js